'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useScroll } from './ScrollProvider';
import gsap from 'gsap';
type MaterializeStage = 'idle' | 'spark' | 'cloud' | 'scan' | 'complete';
const ArchitecturalGrid = t0 => {
  const $ = _c(22);
  const {
    isBlueprint: t1,
    materializeStage: t2,
    skillsProgress: t3,
    labProgress: t4
  } = t0;
  t1 === undefined ? false : t1;
  const materializeStage = t2 === undefined ? "idle" : t2;
  const skillsProgress = t3 === undefined ? 0 : t3;
  const labProgress = t4 === undefined ? 0 : t4;
  const meshRef = useRef(null);
  const materialRef = useRef(null);
  const lineMaterialRef = useRef(null);
  const lightRef = useRef(null);
  const {
    scrollProgress
  } = useScroll();
  const count = 40;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const pos = new Float32Array(4800);
    const chaosPos = new Float32Array(4800);
    const ind = [];
    const types = new Float32Array(1600);
    for (let i = 0; i < 40; i++) {
      for (let j = 0; j < 40; j++) {
        const x = (i / 39 - 0.5) * 80;
        const z = (j / 39 - 0.5) * 80;
        const idx = (i * 40 + j) * 3;
        pos[idx] = x;
        pos[idx + 1] = 0;
        pos[idx + 2] = z;
        chaosPos[idx] = (Math.random() - 0.5) * 300;
        chaosPos[idx + 1] = (Math.random() - 0.5) * 200;
        chaosPos[idx + 2] = (Math.random() - 0.5) * 200;
        types[i * 40 + j] = Math.random() > 0.5 ? 1 : 0;
        const k = i * 40 + j;
        if (i < 39) {
          ind.push(k, k + 40);
        }
        if (j < 39) {
          ind.push(k, k + 1);
        }
      }
    }
    t5 = [pos, new Uint16Array(ind), types, chaosPos];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [positions, indices, binaryTypes, chaosPositions] = t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      uniforms: {
        uTime: {
          value: 0
        },
        uScroll: {
          value: 0
        },
        uColor: {
          value: new THREE.Color("#6366f1")
        },
        uOpacity: {
          value: 0
        },
        uScanProgress: {
          value: -1.2
        },
        uReconstructProgress: {
          value: 0
        }
      },
      vertexShader: "\n      uniform float uTime;\n      uniform float uScroll;\n      uniform float uReconstructProgress;\n      attribute float aBinaryType;\n      attribute vec3 aChaosPosition;\n      varying float vElevation;\n      varying float vWorldY;\n      varying float vProgress;\n      varying float vBinaryType;\n      \n      void main() {\n        // EXPLICIT LINEAR LERP: No side bias possible\n        // Moves directly from aChaosPosition to position (grid)\n        vec3 pos = mix(aChaosPosition, position, uReconstructProgress);\n\n        vec4 modelPosition = modelMatrix * vec4(pos, 1.0);\n        \n        // Wave only starts appearing as grid solidifies\n        float elevation = sin(modelPosition.x * 0.05 + uTime) * \n                         cos(modelPosition.z * 0.05 + uTime) * \n                         min(3.0 + uScroll * 10.0, 12.0) * pow(uReconstructProgress, 2.0);\n        \n        modelPosition.y += elevation;\n        vElevation = elevation;\n        vWorldY = modelPosition.y;\n        vProgress = uReconstructProgress;\n        vBinaryType = aBinaryType;\n        \n        vec4 viewPosition = viewMatrix * modelPosition;\n        vec4 projectedPosition = projectionMatrix * viewPosition;\n        gl_Position = projectedPosition;\n        \n        // Size morphing\n        gl_PointSize = mix(60.0, 4.0, pow(uReconstructProgress, 0.5)); \n      }\n    ",
      fragmentShader: "\n      uniform vec3 uColor;\n      uniform float uOpacity;\n      uniform float uScanProgress;\n      varying float vElevation;\n      varying float vWorldY;\n      varying float vProgress;\n      varying float vBinaryType;\n      \n      float drawBinary(vec2 uv, float type) {\n        uv = uv * 2.0 - 1.0;\n        if (type > 0.5) {\n          return smoothstep(0.1, 0.0, abs(uv.x) - 0.05) * smoothstep(0.8, 0.7, abs(uv.y));\n        } else {\n          float circle = abs(length(uv) - 0.6);\n          return smoothstep(0.1, 0.0, circle) * smoothstep(0.8, 0.7, length(uv));\n        }\n      }\n\n      void main() {\n        float binary = drawBinary(gl_PointCoord, vBinaryType);\n        float circle = 1.0 - smoothstep(0.4, 0.5, length(gl_PointCoord - 0.5));\n        \n        float finalShape = mix(binary, circle, pow(vProgress, 2.0));\n        if (finalShape < 0.1) discard;\n\n        float strength = (vElevation + 12.0) / 24.0;\n        vec3 hotColor = vec3(1.0, 1.0, 1.0);\n        vec3 settledColor = uColor * (0.5 + strength);\n        vec3 baseColor = mix(hotColor, settledColor, vProgress);\n        \n        float normalizedY = vWorldY / 50.0;\n        float scanLine = 1.0 - smoothstep(uScanProgress - 0.1, uScanProgress + 0.1, normalizedY);\n        float scanGlow = exp(-pow(normalizedY - uScanProgress, 2.0) * 200.0);\n        \n        vec3 finalColor = mix(baseColor * 0.2, baseColor, scanLine);\n        finalColor += vec3(1.0, 1.0, 1.0) * scanGlow * 4.0;\n        \n        // Exact original visual for 'complete'\n        if (vProgress > 0.99 && uScanProgress > 1.1) {\n          gl_FragColor = vec4(settledColor, uOpacity);\n          return;\n        }\n\n        float alpha = finalShape * mix(0.8, uOpacity, vProgress);\n        gl_FragColor = vec4(finalColor, alpha);\n      }\n    "
    };
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const shaderArgs = t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...shaderArgs,
      vertexShader: shaderArgs.vertexShader.replace("gl_PointSize =", "//"),
      fragmentShader: "\n      uniform vec3 uColor;\n      uniform float uOpacity;\n      uniform float uScanProgress;\n      uniform float uReconstructProgress;\n      varying float vWorldY;\n      \n      void main() {\n        float normalizedY = vWorldY / 50.0;\n        float scanLine = 1.0 - smoothstep(uScanProgress - 0.1, uScanProgress + 0.1, normalizedY);\n        \n        // Lines fade in with reconstruction\n        float lineOpacity = uOpacity * 0.2 * pow(uReconstructProgress, 2.0);\n        lineOpacity = mix(lineOpacity, uOpacity * 0.2, scanLine);\n        \n        if (uScanProgress > 1.1) lineOpacity = uOpacity * 0.2;\n\n        gl_FragColor = vec4(uColor, lineOpacity);\n      }\n    "
    };
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const lineShaderArgs = t7;
  let t8;
  let t9;
  if ($[3] !== materializeStage) {
    t8 = () => {
      if (!materialRef.current) {
        return;
      }
      if (materializeStage === "spark") {
        materialRef.current.uniforms.uOpacity.value = 0.8;
        gsap.to(lightRef.current, {
          intensity: 800,
          distance: 300,
          duration: 0.4
        });
      } else {
        if (materializeStage === "cloud") {
          gsap.to(lightRef.current, {
            intensity: 0,
            duration: 2,
            delay: 0.5
          });
          gsap.to([materialRef.current.uniforms.uReconstructProgress, lineMaterialRef.current.uniforms.uReconstructProgress], {
            value: 1,
            duration: 3.5,
            ease: "power2.inOut"
          });
        } else {
          if (materializeStage === "scan") {
            gsap.to([materialRef.current.uniforms.uScanProgress, lineMaterialRef.current.uniforms.uScanProgress], {
              value: 1.2,
              duration: 2,
              ease: "power2.inOut"
            });
          } else {
            if (materializeStage === "complete") {
              materialRef.current.uniforms.uScanProgress.value = 1.5;
              materialRef.current.uniforms.uReconstructProgress.value = 1;
              materialRef.current.uniforms.uOpacity.value = 0.4;
              lineMaterialRef.current.uniforms.uOpacity.value = 0.4;
            }
          }
        }
      }
    };
    t9 = [materializeStage];
    $[3] = materializeStage;
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  useEffect(t8, t9);
  let t10;
  if ($[6] !== labProgress || $[7] !== materializeStage || $[8] !== scrollProgress || $[9] !== skillsProgress) {
    t10 = state => {
      if (!materialRef.current) {
        return;
      }
      const time = state.clock.getElapsedTime();
      materialRef.current.uniforms.uTime.value = time;
      lineMaterialRef.current.uniforms.uTime.value = time;
      materialRef.current.uniforms.uScroll.value = scrollProgress;
      lineMaterialRef.current.uniforms.uScroll.value = scrollProgress;
      if (materializeStage === "complete") {
        const combinedProgress = Math.max(skillsProgress, labProgress);
        const fade = Math.max(0, 1 - combinedProgress * 3);
        materialRef.current.uniforms.uOpacity.value = 0.4 * fade;
        lineMaterialRef.current.uniforms.uOpacity.value = 0.4 * fade;
      }
      if (meshRef.current) {
        meshRef.current.rotation.y = time * 0.01;
      }
    };
    $[6] = labProgress;
    $[7] = materializeStage;
    $[8] = scrollProgress;
    $[9] = skillsProgress;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  useFrame(t10);
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [Math.PI / 8, 0, 0];
    t12 = [0, 0, -20];
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <pointLight ref={lightRef} intensity={0} color="#ffffff" position={[0, 0, 0]} />;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <bufferAttribute attach="attributes-position" args={[positions, 3]} />;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <bufferAttribute attach="attributes-aChaosPosition" args={[chaosPositions, 3]} />;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <bufferGeometry>{t14}{t15}<bufferAttribute attach="attributes-aBinaryType" args={[binaryTypes, 1]} /></bufferGeometry>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <points frustumCulled={false}>{t16}<shaderMaterial ref={materialRef} args={[shaderArgs]} transparent={true} blending={THREE.AdditiveBlending} depthWrite={false} /></points>;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <bufferAttribute attach="attributes-position" args={[positions, 3]} />;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <bufferAttribute attach="attributes-aChaosPosition" args={[chaosPositions, 3]} />;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <bufferGeometry>{t18}{t19}<bufferAttribute attach="index" args={[indices, 1]} /></bufferGeometry>;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <group ref={meshRef} rotation={t11} position={t12}>{t13}{t17}<lineSegments frustumCulled={false}>{t20}<shaderMaterial ref={lineMaterialRef} args={[lineShaderArgs]} transparent={true} blending={THREE.AdditiveBlending} depthWrite={false} wireframe={true} /></lineSegments></group>;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  return t21;
};
export { ArchitecturalGrid };
export default ArchitecturalGrid;